import type { ApiPlan } from '@/types/api/plans'

export interface ExternalPlan {
  provider: string
  external_plan_id: string
  price: number
  discount: number
}

export interface ExternalPlans {
  [key: string]: ExternalPlan
}

export interface Price {
  type: string
  price: string
  discount: string | null
  external_plans: ExternalPlan[]
  prorated_price: string
  base_price?: Price
  currency: string
}

export interface PlanDescriptor {
  type: string
  tier: string
  interval?: string
}

export interface PaymentPlan extends PlanDescriptor {
  interval: string
  price_type: string
  price: number
  prorated_price: number
  discount: number
  external_plans: ExternalPlans
  id: number
  credits: number
}

export interface GetPlanArgs extends PlanDescriptor {
  price_type: string
  api_plans: ApiPlan[]
}

export interface GetPlanCostsArgs extends PlanDescriptor {
  api_plans: ApiPlan[]
  cheap?: boolean
}

export interface IntervalTypes {
  base: PaymentPlan | null
  early_bird: PaymentPlan | null
  affiliate: PaymentPlan | null
  discount: PaymentPlan | null
}

export interface PlanOptions {
  cheap?: PaymentPlan
  costly?: PaymentPlan
  options: IntervalTypes
}

export interface PlanIntervals {
  yearly: PlanOptions
  monthly: PlanOptions
}

export interface GmPlan {
  id: string
  starter: PlanIntervals
  premium: PlanIntervals
  elite: PlanIntervals
}

export interface GmPlans {
  cash: GmPlan
  mtt: GmPlan
  spins: GmPlan
  husng: GmPlan
  straddle_ante_cash: GmPlan
}

export interface CommonTierPlanInfo {
  id: string
  plan: PaymentPlan
  rank: number
  time: string
  title: string
  name: string
  full_name: string
  earlyBird: boolean
  affiliate: boolean
  discount: boolean
  bestValue: boolean
}

export interface MonthlyTierPlan extends CommonTierPlanInfo {
  no_discount_month_price: number | null
  month_price: number
  next_month_price: number
  yearlySavings: number
}

export interface YearlyTierPlan extends CommonTierPlanInfo {
  no_discount_month_price: number | null
  month_price: number
  year_price: number
  next_year_price: number
  savings: string
  subtitle: string
}

export interface HandsUsage {
  hands_used: number
  reset_date: string
}
// eslint-disable-next-line no-shadow
export enum PaymentMethod {
  OTHER = 0,
  CARD = 1,
  ALIPAY = 2,
  WECHAT = 3,
  CRYPTO = 4,
}

export interface Subscription {
  public_id: string
  plan: ApiPlan
  price: Price
  external_plan: ExternalPlan | null
  expiration_date: string
  canceled: boolean
  updated_plan: ApiPlan | null
  updated_price: Price | null
  updated_external_plan: ExternalPlan | null
  updated_plan_date: string | null
  hands_usage: HandsUsage
  hands_limit: number
  latest_payment_method: PaymentMethod
}

export type TierPlan = MonthlyTierPlan | YearlyTierPlan

export interface AllTierPlans {
  starter: TierPlan[]
  premium: TierPlan[]
  elite: TierPlan[]
}

export interface SelectedTierPlansArgs extends PlanDescriptor {
  plans: GmPlans
}

export interface TierPlanArgs extends PlanDescriptor {
  selectedTierPlans: TierPlan[]
}
